import createClient, { type Client, type Middleware } from 'openapi-fetch';

import type { paths } from './schema';

// Same-origin client. Resolving to `window.location.origin` at module load
// gives us an absolute URL — needed so the Node-based fetch (undici) used
// inside vitest+jsdom can parse the URL, and harmless in the browser where
// it already matches `baseUrl: '/'` semantics.
//
// In dev the Vite proxy fronts `/api/*` → http://localhost:3000.
// In prod the UI is served from the backend host directly via Caddy.
const baseUrl = typeof window === 'undefined' ? '' : window.location.origin;

// Re-read `globalThis.fetch` on every call so that test-time patches (MSW) take
// effect even if they land after this module is imported.
const dynamicFetch: typeof fetch = (...args) => globalThis.fetch(...args);

export const api: Client<paths> = createClient<paths>({
  baseUrl,
  credentials: 'include',
  fetch: dynamicFetch,
});

// Plan 11-01 (BUG-003): centralised 401 handling. When any non-`/auth/me`
// request comes back unauthorised, the user's session is gone — the wired-in
// handler clears the Zustand store and redirects to `/login`. `/auth/me`
// returning 401 is a normal "not signed in" signal handled by
// `fetchCurrentUser`, so we exclude it. Late binding (handler injected via
// `setUnauthorisedHandler`) keeps `api/client` free of router/store imports
// and gives tests an easy seam.
const unauthorisedHandlerRef: { current: ((path: string) => void) | null } = { current: null };

export const setUnauthorisedHandler = (handler: ((path: string) => void) | null): void => {
  unauthorisedHandlerRef.current = handler;
};

const authMiddleware: Middleware = {
  onResponse({ response, request }) {
    if (response.status !== 401) return undefined;
    const url = new URL(request.url);
    if (url.pathname === '/api/auth/me') return undefined;
    unauthorisedHandlerRef.current?.(url.pathname);
    return undefined;
  },
};

api.use(authMiddleware);
